/**
 * The Acquisition score outside the plain "777" reveal, while the Lumio sections (budgets, campaigns) are hidden:
 * the affiliate program's progress instead of the Lumio levers `acquisition-score.ts` scores.
 *
 * It starts at {@link AFFILIATE_BASE_SCORE} and each step the owner has taken adds {@link AFFILIATE_STEP_POINTS}:
 *   1. `set-up`: the affiliate program is set up (the owner's affiliate account is provisioned);
 *   2. `invite`: an affiliate has joined, or an invite is out (any invite not revoked);
 *   3. `campaign`: an affiliate campaign exists;
 *   4. `registration`: a player registered through affiliates in the last 30 days;
 *   5. `ftd`: a player made a first deposit through affiliates in the last 30 days.
 * All five read 100. The hero's heading names the first step not taken yet, in that order.
 *
 * Pure, so the Acquisition hero and Home's Acquisition gauge compute the same number from the same reads
 * (`useAffiliateProgress`).
 */
import type { Tone } from '../../theme/tokens';
import { acquisitionScoreSummary } from './acquisition-score';

/** The score with no step taken. */
export const AFFILIATE_BASE_SCORE = 50;
/** What each step taken adds. */
export const AFFILIATE_STEP_POINTS = 10;

export type AffiliateStepId = 'set-up' | 'invite' | 'campaign' | 'registration' | 'ftd';

/** The steps, in the order the heading walks them. */
export const AFFILIATE_STEPS: readonly AffiliateStepId[] = ['set-up', 'invite', 'campaign', 'registration', 'ftd'];

/** Which steps the owner has taken. */
export type AffiliateSteps = Record<AffiliateStepId, boolean>;

export const NO_AFFILIATE_STEPS: AffiliateSteps = {
  'set-up': false,
  invite: false,
  campaign: false,
  registration: false,
  ftd: false,
};

export type AffiliateScore = {
  /** 0 to 100 gauge value. */
  score: number;
  /** Gauge-centre band label (the shared `gaugeStatusLabel`). */
  status: string;
  tone: Tone;
  /** The first step not taken yet, or null when all are. */
  nextStep: AffiliateStepId | null;
  /** Hero headline (English copy, the i18n key; the Hero translates it). */
  heading: string;
};

/** Each step's headline, while it is the next one to take. */
const STEP_HEADING: Record<AffiliateStepId, string> = {
  'set-up': 'Set up your affiliate program',
  invite: 'Invite your first affiliate',
  campaign: 'Create an affiliate campaign',
  registration: 'Bring in players through affiliates',
  ftd: 'Turn affiliate players into depositors',
};

export const affiliateAcquisitionScore = (steps: AffiliateSteps): AffiliateScore => {
  const taken = AFFILIATE_STEPS.filter((step) => steps[step]).length;
  const score = AFFILIATE_BASE_SCORE + taken * AFFILIATE_STEP_POINTS;
  const nextStep = AFFILIATE_STEPS.find((step) => !steps[step]) ?? null;
  const { status, tone } = acquisitionScoreSummary(score);
  return { score, status, tone, nextStep, heading: nextStep ? STEP_HEADING[nextStep] : 'Player Acquisition is on track' };
};
